import { fbt } from 'fbtee'
import { Ban, Check, Clock, Flag, Loader2, MoreHorizontal, UserPlus } from 'lucide-react'
import { blockUserLabel } from '../../lib/commonStrings'
import { Button } from '../../components/ui/button'
import { FavoriteStar } from '../../components/FavoriteStar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'

export type FriendState = 'friends' | 'outgoing' | 'incoming' | 'none'

/**
 * The one dominant relationship button on someone's profile (Add friend /
 * Accept / Requested / Friends), the favorite star, and a ⋯ menu with the
 * moderation actions that used to fill the About tab.
 */
export function RelationshipActions({
  friendState,
  busy,
  onAdd,
  onAccept,
  favorite,
  onToggleFavorite,
  canModerate,
  isBlocked,
  onReport,
  onBlock,
  onUnblock,
}: {
  friendState: FriendState
  busy: boolean
  onAdd: () => void
  onAccept: () => void
  favorite: boolean
  onToggleFavorite: () => void
  canModerate: boolean
  isBlocked: boolean
  onReport: () => void
  onBlock: () => void
  onUnblock: () => void
}) {
  const spinner = <Loader2 className="size-4 animate-spin" />

  return (
    <>
      {friendState === 'friends' ? (
        <Button variant="outline" disabled className="h-9 flex-1 disabled:opacity-100">
          <Check className="size-4 text-primary" />
          {fbt('Friends', 'Badge showing the viewed user is a friend of the current user')}
        </Button>
      ) : friendState === 'outgoing' ? (
        <Button variant="outline" disabled className="h-9 flex-1">
          <Clock className="size-4" />
          {fbt('Request sent', 'Label when a friend request is pending on a user profile')}
        </Button>
      ) : friendState === 'incoming' ? (
        <Button onClick={onAccept} disabled={busy} className="h-9 flex-1">
          {busy ? spinner : <UserPlus className="size-4" />}
          {fbt('Accept', 'Button to accept an incoming friend request on a user profile')}
        </Button>
      ) : (
        <Button onClick={onAdd} disabled={busy} className="h-9 flex-1" data-testid="profile-add-friend">
          {busy ? spinner : <UserPlus className="size-4" />}
          {fbt('Add friend', 'Primary button to send a friend request on a user profile')}
        </Button>
      )}
      <FavoriteStar active={favorite} onToggle={onToggleFavorite} />
      {canModerate && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              aria-label={fbt('More options', 'Accessible label of the ⋯ menu on someone else’s profile')}
              data-testid="profile-more-menu"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onReport}>
              <Flag />
              {fbt('Report user', 'Row to report a user on their About tab')}
            </DropdownMenuItem>
            {isBlocked ? (
              <DropdownMenuItem onSelect={onUnblock}>
                <Ban />
                {fbt('Unblock user', 'Row to unblock a user on their About tab')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem variant="destructive" onSelect={onBlock}>
                <Ban />
                {blockUserLabel()}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  )
}
